import {Injectable} from '@angular/core';
import {APIService} from "./api.service";
import {User, UserRole, UserStatus, UserToClient} from "../interfaces/user";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import {BehaviorSubject} from "rxjs";
import {NbToastrService} from "@nebular/theme";
import {APIResponse} from "../interfaces/api-response";
import {PasswordReset} from "../interfaces/password-reset";
import {Paged} from "../interfaces/paged";
import {Client} from "../interfaces/client";
import {environment as env} from "../../environments/environment";
import {Router} from "@angular/router";
import {CookieService} from "ngx-cookie-service";
import {SettingsService} from "./settings.service";
import {Globals} from "./Globals";

@Injectable({
  providedIn: 'root'
})
export class AuthService extends APIService{

  public readonly user: BehaviorSubject<User> = new BehaviorSubject<User>(null);
  public readonly storage = sessionStorage;

  constructor(http: HttpClient,
              toast: NbToastrService,
              router: Router,
              cookie: CookieService,
              private settings: SettingsService) {
    super(http, toast, router, cookie);
  }

  public bootstrap(){
    return this.me.then((user: User) => {
      this.user.next(user);
      return user;
    }).catch((error) => {
      if(this.user.getValue()) {
        return this.logout();
      }
      return null;
    });
  }

  public async myCompanies(page: number = 1): Promise<Paged<Client>>{
    return await this.get<Paged<Client>>('/me/clients?page=' + (page || 1));
  }

  public async switchCompany(company: Client): Promise<User|null>{

    // If we use a webapp frontend we may need to change this, page refresh
    if(company.domain){
      Globals.toDomain(company.domain);
      return null;
    }

    // In app switching, no page refresh. Settings are stored per company and saving replaces the
    // whole blob, so save this company's and load the next one's before anyone can save again.
    await this.settings.saveUserSettings(true).catch(err => console.error(err));
    // Until the next company's load, nothing may save (e.g. the beforeunload beacon) into whichever company the session is on.
    this.settings.clearUserSettings();
    return this.http.post<User>( '/auth/switch', {company: company.id}).toPromise().then(async (response: User) => {
      if(response){
        await this.settings.gatherUserSettings().catch((err) => {
          console.error(err);
          this.settings.clearUserSettings();
        });
        this.user.next(response);
        return response;
      }
      return null;
    }).catch(async (err: HttpErrorResponse) => {
      this.toast.danger(err.error.data, err.error.message);
      // Back on whichever company the session is on now.
      await this.settings.gatherUserSettings().catch(e => console.error(e));
      return null;
    });
  }

  public get me(): Promise<User>{
    return this.http.get<User>(env.api + '/me', Object.assign({}, this.apiOptions)).toPromise()
      .then((me) => {
      if(me && me.id){
        return this.settings.gatherUserSettings().then(() => {
          return me;
        }).catch(err => {
          console.error(err);
          return me;
        });
      }
      return null;
    });
  }

  public get loggedIn(): boolean{
    return this.user.getValue() && this.user.getValue().id != null;
  }

  /**
   * Where an authenticated user belongs when the caller has no explicit
   * ?redirect to honour. Read at NAVIGATION time, never cached: it depends on
   * the user, who is only loaded once the login/bootstrap resolves.
   *
   * /dashboard for a new user because their onboarding form lives there and
   * nowhere else -- DashboardEnabledGuard lets them past the flag for the same
   * reason. /dashboard for everyone else only where the flag is on; it is dark
   * by default in prod (config('dashboard.enabled')) pending QC review, and
   * /market is the safe landing page until it ships.
   */
  public get landingRoute(): string{
    return (this.isNew || this.user.getValue()?.dashboard_enabled) ? '/dashboard' : '/market';
  }

  public get isNew(): boolean{
    const u = this.user.getValue();
    return this.isUser && u && !u.user_to_client?.onboarded_at && u.user_to_client?.status > 0;
  }

  public get isAdmin(): boolean{
    return this.user.getValue() && this.user.getValue().user_to_client?.role === UserRole.ADMIN;
  }

  public get isStaffer(): boolean{
    return this.user.getValue() && this.user.getValue().user_to_client?.role === UserRole.STAFFER;
  }

  public get canInvoice(): boolean{
    return !!this.user.getValue()?.can_invoice;
  }

  public get isUser(): boolean{
    return this.user.getValue() && this.user.getValue().user_to_client?.role === UserRole.USER;
  }

  public get isSuperuser(): boolean{
    return !!this.user.getValue()?.is_super_user;
  }

  public isMe(User_id: number): boolean{
    return this.user.getValue()?.id === User_id;
  }

  public get curClient(): UserToClient|null{
    return this.user.getValue().user_to_client;
  }

  public async register(data): Promise<User>{
    // Another identity from here on: nothing may save the cached settings into it.
    this.settings.clearUserSettings();
    return this.http.post<APIResponse<User>>( '/auth/register', data).toPromise().then(() => {
      return this.bootstrap();
      // return this.updateToken(response.message);
    }).catch((err: HttpErrorResponse) => {
      this.toast.danger(err.error.data, err.error.message);
      this.clear();
      return null;
    });
  }

  public async logout(): Promise<boolean>{
    // Best effort: a failed save must not keep the session alive while the app says logged out.
    if(this.loggedIn) {
      await this.settings.saveUserSettings(true).catch(err => console.error(err));
    }

    return this.http.post<APIResponse<any>>('/auth/logout', null, this.apiOptions).toPromise().then(() => {
      this.clear();
      return true;
    }).catch((error: APIResponse<Error>) => {
      this.clear();
      return error.message === 'Unauthenticated.';
    });
  }

  public async login(username: string, password: string, withkey:string = null): Promise<User>{
    const data: any = {
      username: username,
      password: password
    };

    if(withkey){
      data.with = withkey;
    }

    // Another identity from here on: nothing may save the cached settings into it.
    this.settings.clearUserSettings();
    return this.http.post<APIResponse<User>>( '/auth/login', data).toPromise().then(() => {
      return this.bootstrap();
    }).catch((err: HttpErrorResponse) => {
      this.toast.danger(err.error.data, err.error.message);
      this.clear();
      return null;
    });
  }

  public clear(){
    // this.storage.removeItem('token');
    // The next user must not inherit these, e.g. when their /me/settings load fails.
    this.settings.clearUserSettings();
    this.user.next(null);
  }

  public async resetPassword(username: string): Promise<APIResponse<PasswordReset>>{
    return await this.errorify(this.http.post<any>('/auth/reset', {username}, this.apiOptions).toPromise());
  }

  public async finishResetPassword(rawValue: any): Promise<User> {
    // Another identity from here on: nothing may save the cached settings into it.
    this.settings.clearUserSettings();
    return this.http.post<any>('/auth/reset/finish', rawValue, this.apiOptions).toPromise().then(() => {
      return this.bootstrap();
      // return this.updateToken(response.message);
    }).catch((err: HttpErrorResponse) => {
      this.toast.danger(err.error.data, err.error.message);
      this.clear();
      return null;
    });
  }

  public async invitedUser(target: string) {
    return this.errorify(this.http.get<User>('/auth/invited?id=' + target, this.apiOptions).toPromise());
  }

  public async removeProvider(provider: string) {
    return this.errorify(this.http.post<User>( '/auth/' + provider + '/remove', null).toPromise());
  }
}
